import { interpolateRdYlGn } from 'd3-scale-chromatic'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import s from './Heatmap2D.module.scss'

import { AdSpotCoords, HeatmapScene, heatmapVector } from 'types/heatmap'

import { createHeatmapGradientString } from 'utils/createHeatmapGradientString'
import { format2DHeatmap } from 'utils/format2DHeatmap'

import { useDeviceContext } from 'contexts/DeviceContext'

import { ArrowDownIcon } from 'assets/icons/ArrowDownIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'

import { CheckboxControlled } from 'components/Controls/Checkbox/CheckboxControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'

interface Props {
  resChartData: heatmapVector[]
  approxPoints: heatmapVector[]
  heatmapSizes: [number, number]
  adSpots: AdSpotCoords[]
  level: string
  precision: number
  setPrecision: (val: number) => void
  scene?: HeatmapScene
}

export const Heatmap2D = (props: Props) => {
  const { resChartData, heatmapSizes, adSpots, level, scene, approxPoints, precision, setPrecision } = props
  const [isOpenSettings, setIsOpenSettings] = useState(false)
  const { deviceType } = useDeviceContext()
  const HEATMAP_WIDTH = deviceType === 'laptop' ? 600 : deviceType === 'tablet' ? 400 : 250
  const HEATMAP_HEIGHT = deviceType === 'laptop' ? 600 : deviceType === 'tablet' ? 400 : 250

  const precisionModifier = precision === 3 || precision === 4 ? 1 : 2

  const { control, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      approximate: false,
    },
  })

  const isApprox = watch('approximate')

  const [selected, setSelected] = useState<{ value: number; z: number | null; x: number | null }>({
    value: 0,
    z: null,
    x: null,
  })

  useEffect(() => {
    setSelected({ value: 0, z: null, x: null })
  }, [level, isApprox])

  const [chartData, setChartData] = useState<number[][]>([])
  useEffect(() => {
    setChartData(format2DHeatmap(resChartData, heatmapSizes, approxPoints, isApprox))
  }, [resChartData, heatmapSizes, approxPoints, isApprox])

  const chartMin = useMemo(() => {
    if (chartData) return Math.min(...chartData?.reduce((p, n) => [...p, ...n], [])) || 0
    else return 0
  }, [chartData])

  const chartMax = useMemo(() => {
    if (chartData) return Math.max(...chartData?.reduce((p, n) => [...p, ...n], [])) || 1
    else return 1
  }, [chartData])

  const [width, setWidth] = useState<number[]>([])
  const [length, setLength] = useState<number[]>([])

  useEffect(() => {
    setWidth([])
    const shiftw = scene?.shift_width || 0
    const w = scene?.width || 0
    const size = heatmapSizes?.[0] || 0
    for (let i = shiftw; i <= w; i += (w - shiftw) / size) {
      setWidth((prev) => {
        return [...prev, i]
      })
    }
  }, [scene, heatmapSizes])

  useEffect(() => {
    setLength([])
    const shiftl = scene?.shift_length || 0
    const l = scene?.length || 0
    const size = heatmapSizes?.[1] || 0
    for (let i = shiftl; i <= l; i += (l - shiftl) / size) {
      setLength((prev) => {
        return [i, ...prev]
      })
    }
  }, [scene, heatmapSizes])

  const [xHigh, setXHigh] = useState<number>(0)
  const [xLow, setXLow] = useState<number>(0)

  const [zHigh, setZHigh] = useState<number>(0)
  const [zLow, setZLow] = useState<number>(0)

  useEffect(() => {
    const shiftw = scene?.shift_width || 0
    const w = scene?.width || 0
    const size = heatmapSizes?.[0] || 0

    if (selected.x || selected.x === 0) {
      setXHigh((selected.x * (w - shiftw)) / size + shiftw)
      setXLow(((selected.x + 1) * (w - shiftw)) / size + shiftw)
    }
  }, [selected.x, scene])

  useEffect(() => {
    const shiftl = scene?.shift_length || 0
    const l = scene?.length || 0
    const size = heatmapSizes?.[0] || 0

    if (selected.z || selected.z === 0) {
      setZHigh(((size - selected.z - 1) * (l - shiftl)) / size + shiftl)
      setZLow(((size - selected.z) * (l - shiftl)) / size + shiftl)
    }
  }, [selected.z, scene])

  const gradient = useMemo(() => createHeatmapGradientString(chartMax), [chartMax])

  return (
    <div className={s.contentWrapper}>
      <div className={s.settingsBurger}>
        {deviceType ==='mobile' &&
          <CustomButton variant='outlined' onClick={() => setIsOpenSettings(prev => !prev)} className={s.settingsBtn}>
            Settings
          </CustomButton>
        }
        <div className={`${s.heatmapParams} ${deviceType ==='mobile'? isOpenSettings ? '' : s.hideParams : ''}`}>
          <div className={s.settingsWrapper}>
            <p className={s.precisionLabel}>Precision</p>
            <div className={s.precisionButtonsWrapper}>
              <CustomButton
                small
                className={s.precisionButton}
                variant={precision === 3 ? 'secondary' : 'outlined'}
                onClick={() => setPrecision(3)}
              >
                8x8
              </CustomButton>
              <CustomButton
                small
                className={s.precisionButton}
                variant={precision === 4 ? 'secondary' : 'outlined'}
                onClick={() => setPrecision(4)}
              >
                16x16
              </CustomButton>
              <CustomButton
                small
                className={s.precisionButton}
                variant={precision === 5 ? 'secondary' : 'outlined'}
                onClick={() => setPrecision(5)}
              >
                32x32
              </CustomButton>
            </div>
            <hr className={s.precisionDivider} />
            <div className={s.precisionTooltopWrapper}>
              <InfoIcon className={s.excIcon} />
              <p className={s.precisionTooltip}>Changing of precision resets scene level.</p>
            </div>
          </div>
          <CheckboxControlled
            labelText="Approximate heatmap"
            control={control}
            name="approximate"
            color="secondary"
            disabled={!approxPoints.length}
            style={{ fontSize: '1.25rem' }}
          />
        </div>
      </div>
      <div className={s.heatmapWrapper}>
        <div className={s.verticalAxis} style={{ height: HEATMAP_HEIGHT }}>
          {length?.map((label, ind) =>
            ind % precisionModifier === 0 ? (
              <div
                key={label}
                className={`${s.axisLabel}`}
                style={{
                  minHeight: HEATMAP_HEIGHT / (heatmapSizes?.[0] || 0),
                  maxHeight: HEATMAP_HEIGHT / (heatmapSizes?.[0] || 0),
                }}
              >
                <span>{label.toFixed(2)}</span>
              </div>
            ) : (
              <div
                key={label}
                className={`${s.axisLabel}`}
                style={{
                  minHeight: HEATMAP_HEIGHT / (heatmapSizes?.[0] || 0),
                  maxHeight: HEATMAP_HEIGHT / (heatmapSizes?.[0] || 0),
                }}
              />
            )
          )}
        </div>

        <div className={s.heatmap} style={{ width: HEATMAP_WIDTH, height: HEATMAP_HEIGHT }}>
          {chartData?.map((row, ind) => {
            return (
              <div className={s.row} key={`${ind}`}>
                {row?.map((cell, index) => (
                  <div className={s.cellWrapp} key={`${ind}${index}`}>
                    <div
                      tabIndex={0}
                      style={{
                        backgroundColor: cell
                          ? `${interpolateRdYlGn(1 - (cell - chartMin) / (chartMax - chartMin))}`
                          : 'transparent',
                        height: HEATMAP_HEIGHT / heatmapSizes[0] - 1,
                        width: HEATMAP_WIDTH / heatmapSizes[1] - 1,
                      }}
                      className={s.cell}
                      onClick={(e) => {
                        setSelected({ value: cell, x: index, z: ind })
                      }}
                    />
                    {adSpots?.map((adSpot) =>
                      adSpot.x === index && adSpot.z === ind && (adSpot.part_key === level || level === 'all') ? (
                        <div
                          key={`${adSpot.id}${adSpot.real_x}${adSpot.z}${adSpot.real_z}`}
                          className={` ${s.adspot}`}
                          style={{
                            backgroundColor: '#3888ff',
                            height: HEATMAP_HEIGHT / heatmapSizes[0] - HEATMAP_HEIGHT / heatmapSizes[0] / 2,
                            width: HEATMAP_WIDTH / heatmapSizes[1] - HEATMAP_WIDTH / heatmapSizes[1] / 2,
                          }}
                        />
                      ) : null
                    )}
                    <div className={s.adSpotsInfoWrapper}>
                      {adSpots?.map((adSpot) =>
                        adSpot.x === index && adSpot.z === ind && (adSpot.part_key === level || level === 'all') ? (
                          <div key={`${adSpot.id}${adSpot.real_x}${adSpot.real_z}`} className={s.adSpotInfo}>
                            <p>Ad Space: {adSpot?.name}</p>
                            <p>x: {adSpot.real_x}</p>
                            <p>y: {adSpot.real_y}</p>
                            <p>z: {adSpot.real_z}</p>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
          <div className={`${s.horisontalAxis}`} style={{ width: HEATMAP_WIDTH }}>
            {width?.map((label, ind) =>
              ind % precisionModifier === 0 ? (
                <div
                  key={label}
                  className={`${s.axisLabel} ${s.rotated}`}
                  style={{
                    display: `flex`,
                    width: HEATMAP_WIDTH / (heatmapSizes?.[0] || 0),
                    minWidth: HEATMAP_WIDTH / (heatmapSizes?.[0] || 0),
                  }}
                >
                  {label.toFixed(2)}
                </div>
              ) : (
                <div
                  key={label}
                  className={`${s.axisLabel} ${s.rotated}`}
                  style={{
                    width: HEATMAP_WIDTH / (heatmapSizes?.[0] || 0),
                    minWidth: HEATMAP_WIDTH / (heatmapSizes?.[0] || 0),
                  }}
                />
              )
            )}
          </div>
        </div>

        <div className={s.heatLvlWrapper}>
          <div className={s.heatBarWrapper}>
            <span className={s.heatLvlLabel}>{chartMax}s</span>
            <div
              style={{
                background: `linear-gradient(${gradient})`,

                height: HEATMAP_HEIGHT,
                position: 'relative',
                transition: `all 1s ease-out`,
                zIndex: -2,
              }}
              className={s.gradientBar}
            />
            <span className={s.heatLvlLabel}>{chartMin}s</span>
          </div>
          <div className={s.heatLvlLabelsWrapper}>
            <div className={s.cellHeatLvlContainer} style={{ height: HEATMAP_HEIGHT }}>
              <div
                className={s.cellHeatLvl}
                style={{
                  bottom: (selected.value * HEATMAP_HEIGHT) / chartMax - 36,
                  transition: 'bottom 1s ease-in-out',
                }}
              >
                <ArrowDownIcon style={{ fill: '#fff', width: '1rem', height: '1rem', transform: 'rotate(90deg)' }} />
                <div>
                  <p className={s.cellHeatLabel}>{selected.value}s</p>
                  <>
                    <p>
                      x: {xHigh.toFixed(2) || 0} - {xLow.toFixed(2) || 0}
                    </p>
                    <p>
                      z: {zHigh.toFixed(2) || 0} - {zLow.toFixed(2) || 0}
                    </p>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
